import React, { useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Zoom from "@mui/material/Zoom";
import Alert from "@mui/material/Alert";
import Collapse from "@mui/material/Collapse";
import IconButton from "@mui/material/IconButton";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DownloadIcon from "@mui/icons-material/Download";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";

import Popover from "@mui/material/Popover";
import { FileInput } from "../../../input/FileInput/FileInput";
import {
  MAX_FILE_SIZE,
  ACCEPTED_FILE_TYPES,
  ACCEPTED_FILE_EXTENSIONS,
  TEXT,
  COLUMN_HEADER_IMAGE_URL,
} from "../constants";

interface IdleContentProps {
  onFilesAccepted: (files: File[]) => void;
  onDownloadTemplate: () => void;
  customLimit?: number;
}

export const IdleContent: React.FC<IdleContentProps> = ({
  onFilesAccepted,
  onDownloadTemplate,
  customLimit,
}) => {
  const [instructionsOpen, setInstructionsOpen] = useState(true);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  return (
    <Box>
      {/* Instructions Section */}
      <Alert
        severity="info"
        sx={{ mb: 3 }}
        action={
          <IconButton
            aria-label={instructionsOpen ? "collapse" : "expand"}
            onClick={() => setInstructionsOpen(!instructionsOpen)}
            size="small"
          >
            {instructionsOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        }
      >
        <Stack
          direction="row"
          alignItems="center"
          mb={instructionsOpen ? 1 : 0}
        >
          <Typography variant="subtitle2" fontWeight="bold">
            Instructions
          </Typography>
        </Stack>

        <Collapse in={instructionsOpen}>
          <Box>
            <Typography variant="body2" color="text.secondary" align="justify">
              <span
                dangerouslySetInnerHTML={{
                  __html: TEXT.UPLOAD_POPOVER_INSTRUCTIONS(customLimit),
                }}
              />
            </Typography>

            <Stack direction="row" spacing={1} alignItems="center" mt={1}>
              <Button
                variant="text"
                size="small"
                onClick={onDownloadTemplate}
                startIcon={<DownloadIcon fontSize="small" />}
                sx={{
                  textTransform: "none",
                  p: 0.5,
                  minWidth: "auto",
                }}
              >
                {TEXT.DOWNLOAD_TEMPLATE}
              </Button>

              <Typography
                variant="caption"
                onMouseEnter={(e) => setAnchorEl(e.currentTarget)}
                onMouseLeave={() => setAnchorEl(null)}
                sx={{
                  color: "primary.main",
                  cursor: "pointer",
                  textDecoration: "underline",
                  "&:hover": {
                    color: "primary.dark",
                    textDecoration: "underline",
                  },
                }}
              >
                or see example format...
              </Typography>
            </Stack>
          </Box>
        </Collapse>
      </Alert>

      {/* File Upload Zone */}
      <FileInput
        id="product-csv-upload"
        acceptedFileTypes={ACCEPTED_FILE_TYPES}
        acceptedFileExtensions={ACCEPTED_FILE_EXTENSIONS}
        maxSize={MAX_FILE_SIZE}
        maxFiles={1}
        onFilesAccepted={onFilesAccepted}
        customContent={
          <Box textAlign="center" padding={1} width="100%">
            <Zoom in={true} timeout={500}>
              <Stack
                direction="column"
                justifyContent="center"
                alignItems="center"
                spacing={1}
              >
                <UploadFileIcon fontSize="xlarge" color="secondary" />
                <Typography
                  variant="body2"
                  color="textSecondary"
                  dangerouslySetInnerHTML={{
                    __html: TEXT.UPLOAD_INSTRUCTIONS(customLimit),
                  }}
                />
                <Box marginTop={2}>
                  <Button
                    variant="outlined"
                    sx={{
                      transition: (theme) =>
                        theme.transitions.create(
                          ["background-color", "transform", "color"],
                          {
                            duration: theme.transitions.duration.standard,
                          }
                        ),
                      "&:hover": {
                        transform: "scale(1.05)",
                      },
                    }}
                  >
                    {TEXT.UPLOAD_BUTTON_TEXT}
                  </Button>
                </Box>
              </Stack>
            </Zoom>
          </Box>
        }
      />

      {/* Example Format Popover */}
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
        sx={{ pointerEvents: "none" }}
        disableRestoreFocus
        slotProps={{
          paper: {
            elevation: 8,
            sx: {
              overflow: "visible",
              filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
              mt: 1.5,
              "&::before": {
                content: '""',
                display: "block",
                position: "absolute",
                top: 0,
                left: "50%",
                width: 10,
                height: 10,
                bgcolor: "background.paper",
                transform: "translateX(-50%) translateY(-50%) rotate(45deg)",
                zIndex: 0,
              },
            },
          },
        }}
      >
        <Box sx={{ p: 2 }}>
          <img
            alt={TEXT.COLUMN_HEADERS_ALT}
            src={COLUMN_HEADER_IMAGE_URL}
            style={{
              maxWidth: "400px",
              height: "auto",
              display: "block",
            }}
          />
        </Box>
      </Popover>
    </Box>
  );
};
